import { CloseButton, ToastId } from '@chakra-ui/react'
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
} from '@heroicons/react/outline'

import { ToastType } from 'types'

import PDSFlexBox from 'designSystem/components/PDSFlexBox/PDSFlexBox'
import PDSText from 'designSystem/components/PDSText/PDSText'
import { COLOR } from 'designSystem/foundations/constants/color'
import { FONT_WEIGHT } from 'designSystem/foundations/constants/fontWeight'
import { LINE_HEIGHT } from 'designSystem/foundations/constants/lineHeight'
import { SIZE } from 'designSystem/foundations/constants/size'

type Props = {
  title: string
  description?: string
  type: ToastType
  id?: ToastId
  onClose?: () => void
}

const TOAST_STYLES: Record<
  ToastType,
  { borderColor: string; backgroundColor: string; icon: React.ReactNode }
> = {
  Default: {
    borderColor: COLOR.feedbackInfo_border,
    backgroundColor: COLOR.feedbackInfo,
    icon: (
      <InformationCircleIcon
        color={COLOR.feedbackInfo_border}
        height="1.25rem"
        width="1.25rem"
      />
    ),
  },
  Error: {
    borderColor: COLOR.feedbackAlert_border,
    backgroundColor: COLOR.feedbackAlert,
    icon: (
      <ExclamationCircleIcon
        color={COLOR.feedbackAlert_border}
        height="1.25rem"
        width="1.25rem"
      />
    ),
  },
  Info: {
    borderColor: COLOR.feedbackInfo_border,
    backgroundColor: COLOR.feedbackInfo,
    icon: (
      <InformationCircleIcon
        color={COLOR.feedbackInfo_border}
        height="1.25rem"
        width="1.25rem"
      />
    ),
  },
  Success: {
    borderColor: COLOR.feedbackSuccess_border,
    backgroundColor: COLOR.feedbackSuccess,
    icon: (
      <CheckCircleIcon
        color={COLOR.feedbackSuccess_border}
        height="1.25rem"
        width="1.25rem"
      />
    ),
  },
  Warning: {
    borderColor: COLOR.feedbackWarning_border,
    backgroundColor: COLOR.feedbackWarning,
    icon: (
      <ExclamationCircleIcon
        color={COLOR.feedbackWarning_border}
        height="1.25rem"
        width="1.25rem"
      />
    ),
  },
}

export function CustomToast({ id, onClose, title, description, type }: Props) {
  const styles = TOAST_STYLES[type]

  return (
    <PDSFlexBox
      backgroundColor={styles.backgroundColor}
      border={`1px solid ${styles.borderColor}`}
      borderRadius={SIZE.s8}
      flexDirection="column"
      padding={SIZE.s12}
      width="25rem"
      position="relative"
      minHeight="2rem"
    >
      <CloseButton
        onClick={onClose}
        style={{
          position: 'absolute',
          top: '0.5rem',
          right: '0.5rem',
          color: COLOR.textTertiary,
        }}
      />

      <PDSFlexBox flexDirection="row" alignItems="start" gap={SIZE.s8}>
        <PDSFlexBox>{styles.icon}</PDSFlexBox>
        <PDSFlexBox flexDirection="column">
          <PDSText
            size="md"
            fontWeight={FONT_WEIGHT.semibold}
            lineHeight={LINE_HEIGHT.xs}
            color={COLOR.textPrimary}
          >
            {title}
          </PDSText>
          <PDSText
            size="md"
            fontWeight={FONT_WEIGHT.medium}
            lineHeight={LINE_HEIGHT.xs}
            color={COLOR.textPrimary}
          >
            {description}
          </PDSText>
        </PDSFlexBox>
      </PDSFlexBox>
    </PDSFlexBox>
  )
}
